import { useChartOfAccounts, AccAccount } from '@/features/accounting/hooks/useChartOfAccounts';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Loader2, FolderTree, Landmark, Layers } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';

interface Props {
  searchQuery: string;
  typeFilter: string;
  onEdit?: (group: any) => void;
  page: number;
  onPageChange: (page: number) => void;
}

interface GroupedAccountCategory {
  groupCode: string;
  groupName: string;
  type: string;
  memberAccounts: AccAccount[];
  totalBalanceMinor: number;
  isControl: boolean;
}

export function AccountGroupsList({ searchQuery, typeFilter }: Props) {
  const { accounts, loading, error } = useChartOfAccounts();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64 text-rose-500 font-medium">
        Failed to load Chart of Accounts: {error}
      </div>
    );
  }

  // 1. Group accounts by parent header or main account category
  const groupsMap = new Map<string, GroupedAccountCategory>();

  if (Array.isArray(accounts)) {
    accounts.forEach((acc) => {
      if (!acc || !acc.type || !acc.code) return;
      // Determine group header
      const groupCode = acc.parentCode || (acc.code.length >= 2 ? acc.code.substring(0, 2) + '00' : '0000');
      const groupKey = `${acc.type.toUpperCase()}_${groupCode}`;

      if (!groupsMap.has(groupKey)) {
        const parentAcc = accounts.find((a) => a && a.code === groupCode);
        const groupName = parentAcc?.name || `${acc.type.toUpperCase()} Group (${groupCode})`;
        groupsMap.set(groupKey, {
          groupCode,
          groupName,
          type: acc.type,
          memberAccounts: [],
          totalBalanceMinor: 0,
          isControl: !!acc.isControlAccount || !!parentAcc?.isControlAccount,
        });
      }

      const grp = groupsMap.get(groupKey)!;
      grp.memberAccounts.push(acc);
      grp.totalBalanceMinor += acc.balanceMinor || 0;
    });
  }

  let groupedList = Array.from(groupsMap.values());

  // 2. Apply Filters: Type Filter & Search Query
  if (typeFilter && typeFilter !== 'All') {
    const filterLower = typeFilter.toLowerCase();
    groupedList = groupedList.filter((g) => g.type.toLowerCase() === filterLower || (filterLower === 'capital' && g.type === 'equity'));
  }

  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    groupedList = groupedList.filter(
      (g) =>
        g.groupName.toLowerCase().includes(q) ||
        g.groupCode.includes(q) ||
        g.memberAccounts.some((m) => m.name.toLowerCase().includes(q) || m.code.includes(q)),
    );
  }

  if (groupedList.length === 0) {
    return (
      <div className="bg-white border border-gray-200 border-dashed rounded-xl p-12 flex flex-col items-center justify-center text-center">
        <div className="bg-purple-50 p-4 rounded-full mb-4">
          <FolderTree className="w-8 h-8 text-purple-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No Account Groups Found</h3>
        <p className="text-gray-500 max-w-sm text-sm">
          No chart of accounts categories match your current search query or group filter criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {groupedList.map((grp) => (
          <Card key={grp.groupCode + grp.type} className="border border-border/80 shadow-2xs hover:border-purple-300 dark:hover:border-purple-900 transition-all">
            <CardHeader
              className="border-b border-border/60 pb-3"
              title={
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs px-2 py-0.5 rounded bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 font-bold border border-purple-200">
                      {grp.groupCode}
                    </span>
                    <span className="font-bold text-sm text-foreground truncate">{grp.groupName}</span>
                  </div>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200">
                    {grp.type}
                  </span>
                </div>
              }
            />
            <CardBody className="p-4 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground font-medium flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5 text-purple-600" />
                  Sub-Accounts:
                </span>
                <span className="font-bold text-foreground">{grp.memberAccounts.length} Accounts</span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground font-medium flex items-center gap-1">
                  <Landmark className="w-3.5 h-3.5 text-emerald-600" />
                  Total Net Balance:
                </span>
                <span className="font-mono font-bold text-emerald-600">
                  {formatINR((grp.totalBalanceMinor || 0) / 100)}
                </span>
              </div>

              {/* Sub-account Pills */}
              <div className="pt-2 border-t border-border/50">
                <p className="text-[11px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                  Included Accounts
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
                  {grp.memberAccounts.map((acc) => (
                    <span
                      key={acc._id}
                      className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200/80 flex items-center gap-1"
                    >
                      <span className="font-mono text-[10px] text-purple-600 font-bold">{acc.code}</span>
                      <span className="truncate max-w-[130px]">{acc.name}</span>
                    </span>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}

